import { queryOptions } from '@tanstack/react-query';
import { DocumentServiceFoldersRepository } from 'services/folders/DocumentServiceFoldersRepository';
import { apiDocumentServiceRepository, type GetDocumentById } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import type { ContentFolder } from 'services/folders/entities';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import { findTreeNode } from '@proposify/frontend-utils/findTreeNode';
import { trackError } from '@proposify/frontend-observability';
import { PricingTotalType } from 'services/documents/entities';

export type ContentMetadata = { name: string; isLocked: boolean; totalType: PricingTotalType } & Partial<GetDocumentById>;

const publicFetchContentMetadata = async (contentType: ContentType.DOCUMENT, contentId: string): Promise<ContentMetadata> => {
  if (!contentId) throw new Error(`publicFetchContentMetadata called with empty contentId (contentType: ${contentType})`);
  const doc = await apiDocumentServiceRepository.getDocumentMetadataByContentId({ contentId });
  if (!doc) {
    const error = new Error(`apiDocumentServiceRepository.getDocumentMetadataByContentId failed.`);
    trackError(error, { errorFrom: 'editor-remote', contentId, contentType });
    throw error;
  }
  return { ...doc, name: doc.title };
};

const fetchContentMetadata = async (contentType: ContentType, contentId: string): Promise<ContentMetadata> => {
  if (!contentId) throw new Error(`fetchContentMetadata called with empty contentId (contentType: ${contentType})`);
  if (contentType === ContentType.DOCUMENT) {
    return publicFetchContentMetadata(contentType, contentId);
  }

  // Until we have a metadata endpoint for content using contentId; the minimal information is found via listFolders
  const root = await DocumentServiceFoldersRepository.listFolders({ type: contentType });
  const parentFolder = findTreeNode(root, (folder: ContentFolder) => folder.contents.some((c) => c.id === contentId), 'subfolders');

  if (!parentFolder) {
    const error = new Error(`contentQueries.contentMetadataById failed: cannot find parent folder.`);
    trackError(error, { errorFrom: 'editor-remote', contentId, contentType, folders: root });
    throw error;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const content = parentFolder.contents.find((c) => c.id === contentId)!;
  return { name: content.name, isLocked: false, totalType: PricingTotalType.CALCULATED };
};

export const contentQueries = {
  publicContentMetadataById: (contentType: ContentType.DOCUMENT, contentId: string) => {
    return queryOptions({
      queryKey: QueryKeys.getDocumentMetadataByContentId(contentType, contentId),
      queryFn: () => {
        return publicFetchContentMetadata(contentType, contentId);
      },
      refetchOnWindowFocus: 'always',
    });
  },
  contentMetadataById: (contentType: ContentType, contentId: string) => {
    return queryOptions({
      queryKey: QueryKeys.getDocumentMetadataByContentId(contentType, contentId),
      queryFn: () => {
        return fetchContentMetadata(contentType, contentId);
      },
      refetchOnWindowFocus: contentType === ContentType.DOCUMENT ? 'always' : false,
    });
  },
};
